import { useFlags } from '@flagsmith/flagsmith/react';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { TopLevelFilters } from '../../constants/menu';
import { BreakpointMedia } from '../../utils/config';
import { useNavigateWithUTMs } from '../../hooks/useNavigateWithUTMs';
import { useNewProspectWelcomeToast } from '../../hooks/useNewProspectWelcomeToast';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { CartMinimumBanner } from '../shared/banner/CartMinimumBanner';
import { MenuPage } from './MenuPage';
import { Menu, MenuCartItem } from '../../models/menu.types';
import { clearCache } from '../../services/user/user.queries';
import { useUser } from '../../contexts/UserContext';
import { SubscriptionStatus } from '../../models/user.types';
import { FilterHeader } from './FilterHeader';
import ReactivationBannerContainer from '../shared/banner/ReactivationBannerContainer';
import AdminStatusBannerContainer from '../shared/banner/AdminStatusBannerContainer';
import styles from './MenuPage.module.scss';
import PromoBanner from '../shared/banner/PromoBanner';
import { getFormattedDeliveryDateRange } from '../../utils/dateTimes';
import { useAuth } from '../../contexts/AuthContext';
import LimitedTimeOfferingBanner from '../shared/banner/LimitedTimeOfferingBanner';
import { Toast, ToastVariant } from '../shared/notification/Toast';
import { useNotification } from '../../contexts/NotificationContext';

function MenuPageContainer(): ReactElement {
  const {
    menuWithCartItems,
    isMenuLoading,
    cart,
    addToCart,
    updateCartQuantity,
    upcomingMenus,
    menu,
    recalculateCart,
    setIsCartDrawerOpen,
    selectedFilters,
    setSelectedFilters,
    selectedDropdownFilters,
    setSelectedDropdownFilters,
  } = useMenuWithCart();
  const { showNotification } = useNotification();
  const { isLoggedIn } = useAuth();
  const { navigate } = useNavigateWithUTMs();
  const routerNavigate = useNavigate();
  const remainingMinimumAmount = cart?.priceSummary?.remainingMinimumAmount;
  const location = useLocation();
  const state = location.state as {
    invalidate: boolean,
    invalidateMenus: boolean,
    isUserInitiatedSearch?: boolean,
    isUserInitiatedFilter?: boolean,
  };

  const isUserInitiatedSearch = sessionStorage.getItem('isUserInitiatedSearch') === 'true';
  const isUserInitiatedFilter = sessionStorage.getItem('isUserInitiatedFilter') === 'true';
  const queryClient = useQueryClient();
  const cartIds = upcomingMenus?.menus.filter((upcomingMenu: Menu) => upcomingMenu?.cartId !== null)
    .map((upcomingMenu: Menu) => upcomingMenu?.cartId) || [];
  const { user } = useUser();
  const isMobile = useMediaQuery({ query: BreakpointMedia.MAX_SM });

  const { isActive } = useUser();
  useEffect(() => {
    if (state && state.invalidate === true) {
      clearCache(queryClient, cartIds);
    }
    if (state && state.invalidateMenus === true) {
      queryClient.invalidateQueries({ queryKey: ['menus'] });
    }
  }, [state]);

  useNewProspectWelcomeToast(cart);

  const [displayLoadingShimmer, setDisplayLoadingShimmer] = useState(false);
  const triggerLoadingShimmer = () => {
    setDisplayLoadingShimmer(true);
    // display loading shimmer for 1000 ms
    setTimeout(() => {
      setDisplayLoadingShimmer(false);
    }, 1000);
  };

  // trigger loading shimmer on page load
  useEffect(() => {
    triggerLoadingShimmer();
  }, []);

  const [animateTiles, setAnimateTiles] = useState(false);
  const triggerTileAnimation = () => {
    setAnimateTiles(true);
    // display tile shift for 250 ms
    setTimeout(() => {
      setAnimateTiles(false);
    }, 250);
  };

  const isReactivationBannerVisibile = (): boolean => {
    if (!user?.status) return false;

    switch (user.status) {
      case SubscriptionStatus.CANCELLED:
      case SubscriptionStatus.SUSPENDED:
      case SubscriptionStatus.PAUSED:
      case SubscriptionStatus.RETURNING:
        return true;
      default:
        return !!cart?.isSkipped;
    }
  };
  const displayCartMinimumBanner = (isActive && remainingMinimumAmount && !cart?.isSkipped);
  const [searchParams] = useSearchParams();
  const searchQuery = (searchParams.get('search') ?? '').replace(/^'|'$/g, '');

  // Restore filter state from URL params on mount
  const initialized = useRef(false);
  const initializingFromUrl = useRef(false);
  useEffect(() => {
    const category = searchParams.get('category') as TopLevelFilters | null;
    const tags = searchParams.get('filter_group')
      ?.split(',')
      .filter(Boolean) ?? [];
    const productType = searchParams.get('product_type')
      ?.split(',')
      .filter(Boolean) ?? [];
    const cookTime = searchParams.get('cook_time')
      ?.split(',')
      .filter(Boolean) ?? [];

    const validCategory = (category && Object.values(TopLevelFilters)
      .includes(category))
      ? category as TopLevelFilters
      : null;

    if (validCategory || tags.length) {
      initializingFromUrl.current = true;
      setSelectedFilters({ [validCategory ?? TopLevelFilters.ViewAll]: tags.length ? tags : ['All'] }, false);
    }
    if (productType.length || cookTime.length) {
      initializingFromUrl.current = true;
      setSelectedDropdownFilters({
        productType,
        cookTime,
      }, false);
    }
    initialized.current = true;
  }, []);

  // Sync filter state to URL params whenever filters change
  useEffect(() => {
    if (!initialized.current) return;
    // Skip the first run triggered by restoring state from URL
    if (initializingFromUrl.current) {
      initializingFromUrl.current = false;
      return;
    }

    const params = new URLSearchParams(searchParams);

    const activeCategory = Object.keys(selectedFilters)[0] as TopLevelFilters;
    if (activeCategory && activeCategory !== TopLevelFilters.ViewAll) {
      params.set('category', activeCategory);
    } else {
      params.delete('category');
    }

    const activeTags = (selectedFilters[activeCategory] ?? []).filter((t) => t !== 'All');
    if (activeTags.length) {
      params.set('filter_group', activeTags.join(','));
    } else {
      params.delete('filter_group');
    }

    if (selectedDropdownFilters.productType.length) {
      params.set('product_type', selectedDropdownFilters.productType.join(','));
    } else {
      params.delete('product_type');
    }

    if (selectedDropdownFilters.cookTime.length) {
      params.set('cook_time', selectedDropdownFilters.cookTime.join(','));
    } else {
      params.delete('cook_time');
    }

    const clean = params.toString()
      .replace(/%2C/gi, ',')
      .replace(/%27/gi, '\'');

    routerNavigate(`${location.pathname}?${clean}${location.hash}`, {
      replace: true,
    });
  }, [selectedFilters, selectedDropdownFilters]);

  const menuHeapId = `menu_${menu?.id}`;
  const cartHeapId = `cart_${cart?.id}`;

  const rawDate = menu?.deliveryOn ?? '';
  const prospectBannerHeadline = menu?.prospectBanner?.headline ?? 'Energize with Plant-Powered Recipes';
  // if banner is active & desktop image exists
  const prospectBannerImageUrl = menu?.prospectBanner?.isActive
    ? (menu?.prospectBanner?.imageUrl ?? undefined) : undefined;
  const prospectBannerMobileImageUrl = menu?.prospectBanner?.mobileImageUrl ?? undefined;
  const prospectBannerTextAlign = menu?.prospectBanner?.textAlign ?? 'right';
  const deliverySubheadline = prospectBannerTextAlign === 'left'
    ? `FOR DELIVERY ${getFormattedDeliveryDateRange(rawDate, true)} \u2E3B`
    : `\u2E3B FOR DELIVERY ${getFormattedDeliveryDateRange(rawDate, true)}`;
  const isLastLtoWeek = menu?.id === 1533;

  const isLtoAvailable = useMemo(() => {
    const ltoItem = menuWithCartItems?.find((item) => item.isLtoBox);
    if (isLastLtoWeek && !ltoItem?.isInStock) {
      return false;
    }
    return !!ltoItem;
  }, [menuWithCartItems, isLastLtoWeek]);

  const handleSoldOutLtoClick = () => {
    navigate('/lto/robin-arzon');
    window.scrollTo(0, 0);
  };

  const flags = useFlags(['add-to-cart-subtle']);
  const addToCartSubtle = flags['add-to-cart-subtle'].enabled && flags['add-to-cart-subtle'].value === 'show';

  const handleAddToCartWithToast = async (menuItem: MenuCartItem) => {
    if (cart?.isSkipped) {
      showNotification({
        content: <Toast variant={ToastVariant.CART_UNSKIPPED_AND_ADDED} />,
        duration: 5000,
      });
      await recalculateCart({
        ...cart,
        isSkipped: false,
      });
      queryClient.invalidateQueries({ queryKey: ['menus'] });
    } else if (!isLoggedIn) {
      if (addToCartSubtle && isMobile) {
        showNotification({
          content: <Toast variant={ToastVariant.ITEM_ADDED_TO_CART} />,
          duration: 4000,
        });
        triggerTileAnimation();
      } else {
        setIsCartDrawerOpen(true);
      }
    }
    const suppressDrawer = !isLoggedIn && addToCartSubtle && isMobile;
    addToCart(menuItem, false, suppressDrawer);
  };
  return (
    <div className={styles.menu_page_container} data-heap-id={menuHeapId}>
      {prospectBannerImageUrl && !isLoggedIn && (
        <div className={styles.banner_scroll}>
          <PromoBanner
            subheadline={deliverySubheadline}
            bannerHeadline={prospectBannerHeadline}
            bannerImageUrl={prospectBannerImageUrl}
            bannerMobileImageUrl={prospectBannerMobileImageUrl}
            textColor={menu?.prospectBanner?.textColor ?? 'black'}
            textAlign={menu?.prospectBanner?.textAlign ?? 'right'}
          />
        </div>
      )}
      <div className={styles.banner_and_filter}>
        {displayCartMinimumBanner && (
          <CartMinimumBanner
            remainingCartMinimum={remainingMinimumAmount ?? ''}
            cartMinimum={cart?.priceSummary?.minimumBasePrice ?? ''}
          />
        )}
        {isReactivationBannerVisibile() && <ReactivationBannerContainer />}
        {!displayCartMinimumBanner && !isReactivationBannerVisibile() && (
          <AdminStatusBannerContainer />
        )}
        <div className={styles.filter_header_wrapper}>
          <FilterHeader
            triggerLoadingShimmer={triggerLoadingShimmer}
            triggerTileShiftAnimation={triggerTileAnimation}
          />
        </div>
      </div>

      <div data-heap-id={cartHeapId}>
        <MenuPage
          menuItems={menuWithCartItems}
          onAddToCart={handleAddToCartWithToast}
          onUpdateCartQuantity={updateCartQuantity}
          isSkipped={cart?.isSkipped}
          isMenuLoading={isMenuLoading || displayLoadingShimmer}
          isActive={isActive}
          animateTiles={animateTiles}
          handleSoldOutLtoClick={handleSoldOutLtoClick}
          isLoggedIn={isLoggedIn}
          searchQuery={searchQuery}
          isUserInitiatedSearch={isUserInitiatedSearch}
          isUserInitiatedFilter={isUserInitiatedFilter}
        />
      </div>
    </div>
  );
}

export default MenuPageContainer;
